import {
  Accordion,
  AccordionDetails,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Typography,
  useEventCallback,
} from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import { ExpandMore } from '@mui/icons-material';
import { FormikProps } from 'formik';
import { useState } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { DatePicker } from '@mui/lab';
import PmiTextField from 'shell/components/shared/text-field';
import MunicipalityList, { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import { FiltersFormCohabitation } from 'cohabitation/view-models/filter-cohabitation';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import { useNavigate } from 'react-router-dom';
import FilterCohabitationStreet from './filter-cohabitation';

interface FiltersCohabitationProps {
  filterCohabitationForm: FormikProps<FiltersFormCohabitation>;
  goBackUrl?: string;
}

const FilterCohabitation = ({
  filterCohabitationForm,
  goBackUrl = '/',
}: FiltersCohabitationProps): React.ReactElement => {
  const [open, setOpen] = useState<boolean>(true);

  const navigate = useNavigate();

  const onChangeMunicipalityList = useEventCallback((municipality: MunicipalityAnprVM | null) => {
    filterCohabitationForm.setFieldValue('municipalityOfResidence', municipality);
  });

  const onChangeValues = useEventCallback((values: FiltersFormCohabitation) => {
    filterCohabitationForm.setValues({
      ...filterCohabitationForm.values,
      ...values,
    });
  });

  return (
    <form
      onSubmit={filterCohabitationForm.handleSubmit}
      onReset={filterCohabitationForm.handleReset}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <StickyDiv>
            <div className="pmi-pb-4 pmi-d-block">
              <PmiButton variant="contained" color="cdmBrandThird" type="submit" fullWidth>
                Cerca
              </PmiButton>
            </div>
            <div className="pmi-pb-4 pmi-d-block">
              <PmiButton variant="outlined" color="cdmBrandThird" type="reset" fullWidth>
                Pulisci
              </PmiButton>
            </div>
            <div className="pmi-d-block">
              <PmiButton
                variant="outlined"
                color="cdmBrand"
                fullWidth
                onClick={() => navigate(goBackUrl)}
              >
                Indietro
              </PmiButton>
            </div>
          </StickyDiv>
        </Grid>
        <Grid item xs={12} md={9}>
          <Accordion
            expanded={open}
            onChange={() => {
              setOpen(!open);
            }}
          >
            <AccordionSummary
              aria-controls="panel-cohabitation-filters-content"
              id="panel-cohabitation-filters-header"
              classes={{
                root: 'pmi-flex-row-reverse',
              }}
              expandIcon={<ExpandMore />}
            >
              <Typography>Filtri</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="isNationalResearch"
                        checked={filterCohabitationForm.values.isNationalResearch || false}
                        onChange={() =>
                          filterCohabitationForm.setFieldValue(
                            'isNationalResearch',
                            !filterCohabitationForm.values.isNationalResearch
                          )
                        }
                        inputProps={{ 'aria-label': 'Ricerca nazionale' }}
                      />
                    }
                    label="Ricerca nazionale"
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <MunicipalityList
                    value={filterCohabitationForm.values.municipalityOfResidence || null}
                    onChange={onChangeMunicipalityList}
                    label="Comune di residenza"
                    id="municipalityOfResidence"
                    disabled={filterCohabitationForm.values.isNationalResearch}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <PmiTextField
                    aria-label="Provincia"
                    label="Provincia"
                    id="provinceOfBirth"
                    name="provinceOfBirth"
                    fullWidth
                    type="text"
                    disabled
                    variant="outlined"
                    value={filterCohabitationForm.values.municipalityOfResidence?.province || ''}
                  />
                </Grid>
                <FilterCohabitationStreet
                  values={filterCohabitationForm.values}
                  onChange={filterCohabitationForm.setFieldValue}
                  errors={filterCohabitationForm.errors}
                  touched={filterCohabitationForm.touched}
                  onChangeValues={onChangeValues}
                />
                <Grid item xs={12} md={5}>
                  <PmiTextField
                    label="Identificativo del Comune"
                    aria-label="Identificativo del Comune"
                    fullWidth
                    inputProps={{
                      maxLength: '4',
                    }}
                    onChange={(event) => {
                      filterCohabitationForm.setFieldValue('codeMunicipality', event?.target.value);
                    }}
                    id="code-municipality"
                    value={filterCohabitationForm.values.codeMunicipality || ''}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <PmiTextField
                    label="Cod. ISTAT Comune"
                    aria-label="Codice ISTAT Comune"
                    fullWidth
                    id="code-istat-municipality"
                    inputProps={{
                      maxLength: '6',
                    }}
                    onChange={(event) => {
                      filterCohabitationForm.setFieldValue('codeIstat', event?.target.value);
                    }}
                    value={
                      filterCohabitationForm.values.codeIstat ||
                      filterCohabitationForm.values.municipalityOfResidence?.id ||
                      ''
                    }
                    disabled={!!filterCohabitationForm.values.municipalityOfResidence?.id}
                    helperText={filterCohabitationForm.errors.codeIstat}
                    error={
                      filterCohabitationForm.touched.codeIstat &&
                      Boolean(filterCohabitationForm.errors.codeIstat)
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12} md={5}>
                  <PmiTextField
                    label="Identificativo ANPR"
                    aria-label="Identificativo ANPR"
                    id="identifier-anpr"
                    fullWidth
                    inputProps={{
                      maxLength: '15',
                    }}
                    onChange={(event) => {
                      filterCohabitationForm.setFieldValue('codeAnpr', event?.target.value);
                    }}
                    value={filterCohabitationForm.values.codeAnpr || ''}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item container spacing={2} xs={12}>
                  <Grid item xs={12} md={5}>
                    <PmiTextField
                      aria-label="Codice scheda"
                      label="Codice scheda"
                      id="cardCode"
                      name="cardCode"
                      fullWidth
                      type="text"
                      disabled
                      variant="outlined"
                      value={filterCohabitationForm.values.cardCode || ''}
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <DatePicker
                      label="Data di riferimento scheda"
                      aria-label="Data di riferimento scheda"
                      value={filterCohabitationForm.values.dateForm || null}
                      onChange={(date) => {
                        filterCohabitationForm.setFieldValue('dateForm', date);
                      }}
                      renderInput={(params) => (
                        <PmiTextField id="dateForm" {...params} fullWidth helperText="" />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
    </form>
  );
};

FilterCohabitation.displayName = 'Filters Cohabitation component';

export default FilterCohabitation;
